<template>
  <v-autocomplete
    ref="addressAutocomplete"
    no-filter
    v-model="address"
    :clearable="!readonly"
    v-model:search="query"
    :items="items"
    item-title="address"
    return-object
    @update:model-value="flagForUpdate"
    :label="label"
    :dark="dark"
    attach
    :placeholder="placeholder"
    persistent-placeholder
    :error-messages="errorMsg"
    :loading="loading || fetching"
    :hide-no-data="query === null || query === '' || query?.length === 1"
    :no-data-text="
      fetching ? 'Loading' : 'Nothing matches your search criteria'
    "
    :readonly="readonly"
    @click:clear="() => emit('clear')"
    @click="() => emit('click')"
    bg-color="transparent"
    menu-icon=""
    clear-on-select
  >
    <template v-slot:item="{ props, index, item }">
      <div
        v-bind="props"
        :key="generateUuid() + index"
        style="
          padding: 8px 20px;
          overflow: hidden;
          width: 100%;
          cursor: pointer;
        "
        :style="width ? { 'max-width': width + 'px' } : ''"
      >
        <div
          style="
            border-bottom: 1px solid lightgray;
            display: flex;
            align-items: center;
            padding: 0 4px 8px 4px;
            overflow: hidden;
            width: 100%;
          "
        >
          <v-icon
            v-if="item.raw.localId"
            color="primary"
            style="
              flex-grow: 0;
              flex-shrink: 0;
              margin-right: 8px;
              padding-left: 0;
            "
          >
            mdi-bookmark-outline
          </v-icon>
          <v-icon
            v-else-if="item.raw.isHistoryItem"
            color="primary"
            style="
              flex-grow: 0;
              flex-shrink: 0;
              margin-right: 8px;
              padding-left: 0;
            "
          >
            mdi-progress-clock
          </v-icon>
          <div
            style="
              overflow: hidden;
              text-overflow: ellipsis;
              word-wrap: none !important;
              flex-shrink: 1;
              flex-grow: 1;
              white-space: nowrap;
            "
          >
            {{ item.raw.address }}
          </div>
        </div>
      </div>
    </template>
  </v-autocomplete>
</template>
<script lang="ts" setup>
import { fetchLocationAutoCompleteDetails } from "@/api/addressLookups/calls/address-autocomplete-calls";
import Coordinate from "@/domains/coordinates/coordinate";
import { useMapStore } from "@/stores/mapStore";
import debounce from "lodash.debounce";
import { v4 as uuid } from "uuid";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import { useSavedLocationStore } from "../locations/savedLocationStore";
import { useAddressLookupStore } from "./addressLookupStore";
import processAddressSearchResults from "./processAddressSearchResults";
import type {
  AddressAutocompleteInputUpdateObj,
  AutoCompleteReturnObject,
  processedAddressObj,
} from "./types";

const emit = defineEmits({
  clear: null,
  click: null,
  append: null,
  update: (updateObj: AddressAutocompleteInputUpdateObj) => !!updateObj,
});

interface Props {
  id?: string;
  initialValue?: processedAddressObj;
  label?: string;
  dark?: boolean;
  errorMsg?: string | null;
  loading?: boolean;
  geoLocation?: Coordinate;
  allowFavLocations?: boolean;
  placeholder?: string;
  readonly?: boolean;
}

const {
  id = "",
  initialValue = undefined,
  label = "",
  dark = false,
  errorMsg = null,
  loading = false,
  geoLocation = undefined,
  allowFavLocations = false,
  placeholder = "Enter an address",
  readonly = false,
} = defineProps<Props>();

const addressAutocomplete = useTemplateRef("addressAutocomplete");
const width = ref(400);

const calculateWidth = () => {
  const clientWidth = addressAutocomplete.value?.$el.clientWidth;
  width.value = clientWidth;
};

const addResizeListener = () => {
  if (addressAutocomplete.value) {
    window.addEventListener("resize", calculateWidth);
  }
};

const removeResizeListener = () => {
  if (addressAutocomplete.value) {
    window.removeEventListener("resize", calculateWidth);
  }
};

onMounted(() => {
  addResizeListener();
  calculateWidth();
});

onUnmounted(() => {
  removeResizeListener();
});

watch(
  () => addressAutocomplete,
  () => {
    calculateWidth();
  },
);

const addressLookupStore = useAddressLookupStore();
const mapStore = useMapStore();

const userGeoLocation = computed(() => mapStore.geoIp);
const pannedCenter = computed(() => mapStore.pannedCenter);

const savedLocationsStore = useSavedLocationStore();

const savedLocations = computed(() => savedLocationsStore.getSavedLocations);

const address = ref<processedAddressObj | undefined>(undefined);
const items = ref<processedAddressObj[]>([]);
const query = ref<undefined | string>(undefined);
const results = ref<AutoCompleteReturnObject[] | null>(null);
const initialLoad = ref(true);
const fetching = ref(false);

function generateUuid() {
  return uuid();
}

watch(
  query,
  debounce(async (val) => {
    if (initialLoad.value && initialValue) {
      initialLoad.value = false;
      return;
    }
    if (initialLoad.value) initialLoad.value = false;
    if (val) {
      fetching.value = true;
      fetchLocationAutoCompleteDetails(
        val,
        getBiasLocation(),
        geoLocation || pannedCenter ? 0.5 : undefined,
      )
        .then((data) => {
          results.value = data;
          fetching.value = false;
        })
        .catch((err) => {
          console.error(err);
          fetching.value = false;
        });
    }
  }, 300),
);

watch(results, () => {
  // convert results to items
  if (results.value && query.value) {
    items.value = [];
    const processedRes = processAddressSearchResults(results.value);
    const processedFav = savedLocations.value.map(
      (fav) => fav.toProcessedAddressObj,
    );
    const filteredFav = query.value
      ? processedFav.filter((fav) =>
          fav.address
            .toLowerCase()
            .replace(",", "")
            .includes((query.value || "").toLowerCase().replace(",", "")),
        )
      : processedFav;
    const filteredHistory = query.value
      ? searchHistory.value.filter((item) =>
          item.address
            .toLowerCase()
            .replace(",", "")
            .includes((query.value || "").toLowerCase().replace(",", "")),
        )
      : searchHistory.value;
    items.value = allowFavLocations
      ? [...filteredFav, ...processedRes, ...filteredHistory]
      : processedRes;
  }
  // clear fetching flag
  fetching.value = false;
});

watch(
  () => initialValue,
  (val) => {
    if (!initialLoad.value) {
      if (val && val.address != "") {
        items.value = [val];
      }
      address.value = val;
    }
  },
);

function flagForUpdate() {
  if (
    address.value &&
    address.value.address &&
    !address.value.localId &&
    !address.value.isHistoryItem
  ) {
    addressLookupStore.addToAddressSearchHistory(address.value); // add to search history
  }
  emit("update", {
    id: id,
    addressData: address.value,
  });
}

function setInitialValues() {
  if (initialValue) {
    address.value = initialValue;
    if (address.value.address != "") {
      items.value = [initialValue];
    }
  }

  const tempItems: processedAddressObj[] = [];
  if (address.value && address.value.address != "") {
    tempItems.push(address.value);
  }
  if (savedLocations.value.length > 0) {
    tempItems.push(
      ...savedLocations.value.map((item) => item.toProcessedAddressObj),
    );
  }
  if (searchHistory.value.length > 0) {
    tempItems.push(
      ...searchHistory.value.map((item) => ({ ...item, isHistoryItem: true })),
    );
  }

  items.value = tempItems;
  nextTick(() => {
    initialLoad.value = false;
  });
}

onMounted(() => {
  nextTick(() => {
    setInitialValues();
  });
});

function getBiasLocation(): Coordinate | undefined {
  // prioritize passed location over user panned map display central location over programmatically sourced user location.
  if (geoLocation) return geoLocation; // use passed location.
  if (pannedCenter.value) return pannedCenter.value; // use map display center.
  return userGeoLocation.value ?? undefined; // use ip sourced location.
}

const searchHistory = computed<processedAddressObj[]>(() => {
  return addressLookupStore.addressSearchHistory;
});
</script>
<style scoped>
:deep(
  .v-field__input input::placeholder,
  input.v-field__input::placeholder,
  textarea.v-field__input::placeholder
) {
  font-style: italic;
  color: #161616;
  opacity: 1;
}

:deep(.v-field__input > input) {
  padding-left: 2px !important;
}
:deep(.v-field__input) {
  padding-left: 2px !important;
}

.pwt__address_autocomplete__list_item:hover {
  background-color: #eaf7ff !important;
}
</style>
